import { FC, useCallback, useMemo } from 'react';

import { Routes } from '@common/constants';
import {
  EntitiesListingWrapper,
  JourneyPickerInnerWrapper,
  JourneyPickerOuterWrapper,
} from '@features/EntitiesListing/EntitiesListings.styles';
import { JourneyPickerV2 } from '@features/JourneyPickerV2';
import { useCategoryNavigation } from '@features/JourneyPickerV2/useCategoryNavigation';
import { useNavCategories } from '@features/JourneyPickerV2/useNavCategories';
import { useLabels } from '@features/Menu/useLabels';
import { useRates } from '@features/rates/queries';
import { RatesFilterType } from '@features/rates/types';
import {
  useGetAllSpaPackages,
  useGetSpaClasses,
  useGetSpaClassesPage,
  useGetSpaDaysPage,
  useGetSpaFacilities,
  useGetSpaFacilitiesPage,
  useGetSpaSettings,
  useGetSpaStaysPage,
  useGetSpaTreatments,
  useGetSpaTreatmentsPage,
} from '@features/spa/hooks';
import { useTranslations } from '@lang';
import { parseDateToString } from '@utils/helpers';
import { some } from '@utils/some';
import { useRouter } from 'shared-routing';
import type { SpaLandingSection } from 'voyager';
import { SpaLandingTemplate } from 'voyager';

import { mapRateToItem } from '../mappers/mapRateToItem';
import type { SpaLandingPageV2Props } from './SpaLandingPageV2.types';

export const SpaLandingPageV2: FC<SpaLandingPageV2Props> = ({
  property,
  spaPage,
  journeyParams,
  isSpaDaysEnabled,
  isTreatmentEnabled,
  isFacilitiesEnabled,
  isClassEnabled,
  userHasAccessToSpaClasses,
  language,
}) => {
  const router = useRouter();
  const { t } = useTranslations();
  const labels = useLabels();

  const navCategories = useNavCategories();
  const handleNavCategoryChange = useCategoryNavigation();
  const handleSearchComplete = useCallback(() => {
    handleNavCategoryChange('spa');
  }, [handleNavCategoryChange]);

  const { data: spaSettings } = useGetSpaSettings(property.id);
  const isStaysEnabled = spaSettings?.areSpaStaysEnabled ?? false;

  const { rates: spaStayRates } = useRates({
    fromDate: parseDateToString(journeyParams.fromDate),
    toDate: parseDateToString(journeyParams.toDate ?? journeyParams.fromDate),
    adults: journeyParams.adults,
    children: journeyParams.children,
    childAges: journeyParams.childAges,
    rooms: journeyParams.rooms ?? [],
    type: RatesFilterType.SPA_STAYS,
    enabled: isStaysEnabled,
    language,
  });
  const spaStayItems = spaStayRates.map(mapRateToItem);

  const { data: spaStaysPage } = useGetSpaStaysPage(property.id, language);

  const { data: spaPackages = [] } = useGetAllSpaPackages(property.id, language);

  const { spaTreatments = [] } = useGetSpaTreatments(property.id, language, null, null);

  const { data: spaFacilities = [] } = useGetSpaFacilities(property.id, language, null, null);

  const { data: spaClasses } = useGetSpaClasses(
    property.id,
    language,
    parseDateToString(journeyParams.spaDate)
  );

  const { data: spaClassesPage } = useGetSpaClassesPage(property.id, language);
  const { data: spaDaysPage } = useGetSpaDaysPage(property.id, language);
  const { data: spaTreatmentsPage } = useGetSpaTreatmentsPage(property.id, language);
  const { data: spaFacilitiesPage } = useGetSpaFacilitiesPage(property.id, language);

  const showClasses = isClassEnabled && userHasAccessToSpaClasses && some(spaClasses);

  const sections = useMemo(() => {
    const result: SpaLandingSection[] = [];

    if (isStaysEnabled && spaStayItems.length > 0) {
      result.push({
        id: 'spa-stays',
        title: spaStaysPage?.title || labels?.spastays || t('spaLanding.spaStays'),
        items: spaStayItems.map((item) => ({
          id: String(item.id),
          name: item.name,
          imageUrl: item.image?.url,
          price: item.price ?? null,
        })),
        onTitleClick: () => router.push({ pathname: Routes.SPA_STAYS_LIST }),
        onSelectItem: (id: string) => router.push({ pathname: Routes.RATE_DETAIL, query: { id } }),
      });
    }

    if (isSpaDaysEnabled && spaPackages.length > 0) {
      result.push({
        id: 'spa-days',
        title: spaDaysPage?.title || labels?.spadays || t('category.spaDays'),
        items: spaPackages.map((item) => ({
          id: String(item.id),
          name: item.name,
          imageUrl: item.image?.url,
          price: item.price ?? null,
          discount: item.discount,
          duration: item.durationText ? t(item.durationText) : undefined,
          promotionName: item.discount?.name,
        })),
        onTitleClick: () => router.push({ pathname: Routes.SPA_DAY_LIST }),
        onSelectItem: (id: string) =>
          router.push({ pathname: Routes.SPA_DAY_DETAIL, query: { id } }),
      });
    }

    if (isTreatmentEnabled && spaTreatments.length > 0) {
      result.push({
        id: 'treatments',
        title: spaTreatmentsPage?.title || labels?.treatments || t('category.treatments'),
        items: spaTreatments.map((item) => ({
          id: String(item.id),
          name: item.name,
          imageUrl: item.image?.url,
          price: item.price ?? null,
          discount: item.discount,
          duration: item.duration ? `${item.duration} min` : undefined,
          promotionName: item.discount?.name,
        })),
        onTitleClick: () => router.push({ pathname: Routes.SPA_TREATMENT_LIST }),
        onSelectItem: (id: string) =>
          router.push({ pathname: Routes.SPA_TREATMENT_DETAIL, query: { id } }),
      });
    }

    if (isFacilitiesEnabled && spaFacilities.length > 0) {
      result.push({
        id: 'facilities',
        title: spaFacilitiesPage?.title || labels?.facilities || t('app.facilities'),
        items: spaFacilities.map((item) => ({
          id: String(item.id),
          name: item.name,
          imageUrl: item.image?.url,
          price: item.price ?? null,
          discount: item.discount,
          duration: item.duration ? `${item.duration} min` : undefined,
          promotionName: item.discount?.name,
        })),
        onTitleClick: () => router.push({ pathname: Routes.FACILITY_LIST }),
        onSelectItem: (id: string) =>
          router.push({ pathname: Routes.FACILITY_DETAIL, query: { id } }),
      });
    }

    return result;
  }, [
    isStaysEnabled,
    spaStayItems,
    spaStaysPage,
    isSpaDaysEnabled,
    spaPackages,
    spaDaysPage,
    isTreatmentEnabled,
    spaTreatments,
    spaTreatmentsPage,
    isFacilitiesEnabled,
    spaFacilities,
    spaFacilitiesPage,
    labels,
    t,
    router,
  ]);

  const classesBanner = showClasses
    ? {
        title: spaClassesPage?.title || labels?.classes || t('common.classes'),
        imageUrl: spaClassesPage?.heroBackgroundImage?.url,
        buttonLabel: t('common.view.plain'),
        onSelect: () => router.push({ pathname: Routes.SPA_CLASSES_LIST }),
      }
    : undefined;

  return (
    <EntitiesListingWrapper>
      <JourneyPickerOuterWrapper>
        <JourneyPickerInnerWrapper>
          <JourneyPickerV2
            dateMode="single"
            dateContext="spa"
            categories={navCategories}
            category="spa-days"
            onCategoryChange={handleNavCategoryChange}
            onSearchComplete={handleSearchComplete}
          />
        </JourneyPickerInnerWrapper>
      </JourneyPickerOuterWrapper>
      <SpaLandingTemplate
        heading={spaPage?.title || t('app.spa')}
        sections={sections}
        classesBanner={classesBanner}
        fromPriceLabel={t('common.from')}
        soldOutLabel={t('common.unavailable')}
        noImageLabel={t('spa.noImage')}
        noResultsLabel={t('app.noItemsFound')}
      />
    </EntitiesListingWrapper>
  );
};
